import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileText, Download, Truck, Search, Printer, CheckSquare, Square, Package, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDeliveryNotes() {
    const { user } = useAuth();
    const isSuperAdmin = user?.is_super_admin === true;
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());

    const loadNotes = useCallback(async () => {
        try {
            const res = await adminAPI.getDeliveryNotes();
            setNotes(res.data.delivery_notes || []);
        } catch (err) {
            toast.error('Erreur chargement des BL');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadNotes(); }, [loadNotes]);

    const filtered = notes.filter(n =>
        !search ||
        (n.bl_number || '').toLowerCase().includes(search.toLowerCase()) ||
        (n.supplier_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (n.client_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (n.client_order_number || '').toLowerCase().includes(search.toLowerCase())
    );

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selected.size === filtered.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filtered.map(n => n.id)));
        }
    };

    const handleDownload = async (note) => {
        try {
            const res = await adminAPI.downloadSingleBL(note.id);
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${note.bl_number || 'BL'}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error('Erreur telechargement');
        }
    };

    const handleBatchDownload = () => {
        if (selected.size === 0) return;
        const selectedNotes = filtered.filter(n => selected.has(n.id));
        const csvHeader = 'N BL;Code;Client;Code Client;Commande;Articles;Total EUR;Date generation\n';
        const csvRows = selectedNotes.map(n => {
            const date = n.bl_generated_at ? new Date(n.bl_generated_at).toLocaleDateString('fr-FR') : '';
            return `${n.bl_number || ''};${n.supplier_code || ''};${n.client_name || ''};${n.client_code || ''};${n.client_order_number || ''};${n.items?.length || 0};${(n.delivered_total || n.total || 0).toFixed(2)};${date}`;
        }).join('\n');
        const blob = new Blob(['\uFEFF' + csvHeader + csvRows], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'liste-bons-de-livraison.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Liste de ${selected.size} BL exportee`);
    };

    const handlePrintList = () => {
        if (selected.size === 0) return;
        const selectedNotes = filtered.filter(n => selected.has(n.id));
        const escape = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
        const rows = selectedNotes.map(n => {
            const date = n.bl_generated_at ? new Date(n.bl_generated_at).toLocaleDateString('fr-FR') : '';
            return `<tr><td>${escape(n.bl_number)}</td><td>${escape(n.supplier_code)}</td><td>${escape(n.client_name)}</td><td>${escape(n.client_code)}</td><td>${escape(n.client_order_number)}</td><td style="text-align:center">${n.items?.length || 0}</td><td style="text-align:right">${(n.delivered_total || n.total || 0).toFixed(2)} EUR</td><td>${escape(date)}</td></tr>`;
        }).join('');
        const total = selectedNotes.reduce((s, n) => s + (n.delivered_total || n.total || 0), 0);
        const html = `<!DOCTYPE html><html><head><title>Liste BL</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#1A4D2E;font-size:20px}table{width:100%;border-collapse:collapse;margin-top:15px}th{background:#1A4D2E;color:white;padding:8px 6px;font-size:11px;text-align:left}td{padding:6px;border-bottom:1px solid #ddd;font-size:11px}tr:nth-child(even){background:#f8f8f8}tfoot td{font-weight:bold;border-top:2px solid #1A4D2E;padding-top:10px}.header{display:flex;justify-content:space-between;align-items:center}.date{color:#666;font-size:12px}</style></head><body><div class="header"><h1>PRIXDEAL - Liste des Bons de Livraison</h1><span class="date">Imprime le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span></div><table><thead><tr><th>N BL</th><th>Code</th><th>Client</th><th>Code Client</th><th>Commande</th><th>Articles</th><th>Total</th><th>Date</th></tr></thead><tbody>${rows}</tbody><tfoot><tr><td colspan="5"></td><td style="text-align:center">${selectedNotes.reduce((s,n) => s + (n.items?.length || 0), 0)}</td><td style="text-align:right">${total.toFixed(2)} EUR</td><td></td></tr></tfoot></table></body></html>`;
        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.left = '-9999px';
        printFrame.srcdoc = html;
        printFrame.onload = () => { printFrame.contentWindow.print(); setTimeout(() => document.body.removeChild(printFrame), 1000); };
        document.body.appendChild(printFrame);
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) {
            toast.error('Aucun BL selectionne');
            return;
        }
        if (!window.confirm(`Etes-vous sur de vouloir supprimer ${selected.size} BL ? Cette action est irreversible.`)) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await adminAPI.bulkDeleteDeliveryNotes({ ids: Array.from(selected) });
            toast.success(res.data.message || `${selected.size} BL supprime(s)`);
            setSelected(new Set());
            await loadNotes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };
    const handleBulkDeleteAll = async () => {
        if (!window.confirm('SUPPRIMER TOUS LES BL ? Cette action est irreversible.')) {
            return;
        }
        setBulkDeleting(true);
        try {
            const res = await adminAPI.bulkDeleteDeliveryNotes({ all: true });
            toast.success(res.data.message || 'Tous les BL supprimes');
            setSelected(new Set());
            await loadNotes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;

    return (
        <div className="space-y-6" data-testid="admin-delivery-notes">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-jungle">Bons de Livraison</h1>
                    <p className="text-sm text-muted-foreground mt-1">Tous les BL generes par les fournisseurs</p>
                </div>
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-jungle" />
                    <span className="text-2xl font-bold text-jungle">{notes.length}</span>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher par N BL, fournisseur, client..."
                        className="pl-9 rounded-xl"
                        data-testid="search-bl"
                    />
                </div>
                {filtered.length > 0 && (
                    <>
                        <Button variant="outline" size="sm" onClick={toggleSelectAll} data-testid="select-all-bl">
                            {selected.size === filtered.length ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
                            {selected.size === filtered.length ? 'Deselectionner' : 'Tout selectionner'}
                        </Button>
                        {selected.size > 0 && (
                            <>
                                <Button size="sm" onClick={handlePrintList} variant="outline" data-testid="print-selected-bl">
                                    <Printer className="w-4 h-4 mr-1" />
                                    Imprimer ({selected.size})
                                </Button>
                                <Button size="sm" onClick={handleBatchDownload} className="bg-jungle hover:bg-jungle/90 text-white" data-testid="download-selected-bl">
                                    <Download className="w-4 h-4 mr-1" />
                                    Exporter ({selected.size})
                                </Button>
                                {isSuperAdmin && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleBulkDelete}
                                        disabled={bulkDeleting}
                                        className="border-red-300 text-red-700 hover:bg-red-100"
                                        data-testid="bulk-delete-bl"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        Supprimer ({selected.size})
                                    </Button>
                                )}
                            </>
                        )}
                        {isSuperAdmin && filtered.length > 0 && selected.size === 0 && (
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={handleBulkDeleteAll}
                                disabled={bulkDeleting}
                                data-testid="bulk-delete-all-bl"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Tout supprimer
                            </Button>
                        )}
                    </>
                )}
            </div>

            {selected.size > 0 && (
                <Card className="rounded-xl border-jungle/30 bg-jungle/5">
                    <CardContent className="p-3 flex items-center gap-3">
                        <Package className="w-5 h-5 text-jungle" />
                        <span className="text-sm font-medium text-jungle">{selected.size} BL selectionne(s)</span>
                    </CardContent>
                </Card>
            )}

            {filtered.length === 0 ? (
                <Card className="rounded-xl">
                    <CardContent className="py-12 text-center">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-muted-foreground">{search ? 'Aucun resultat' : 'Aucun bon de livraison genere'}</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filtered.map(note => (
                        <Card
                            key={note.id}
                            className={`rounded-xl transition-shadow cursor-pointer ${selected.has(note.id) ? 'ring-2 ring-jungle/50 shadow-md bg-jungle/5' : 'hover:shadow-md'}`}
                            data-testid={`admin-bl-${note.id}`}
                            onClick={() => toggleSelect(note.id)}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected.has(note.id) ? 'bg-jungle border-jungle' : 'border-gray-300'}`}>
                                            {selected.has(note.id) && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1">
                                                <span className="font-bold text-jungle">{note.bl_number}</span>
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <Truck className="w-3 h-3" /> Livre
                                                </span>
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-mono">Code fournisseur : {note.supplier_code}</span>
                                            </div>
                                            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-sm text-gray-600">
                                                <div><span className="text-gray-400">Client :</span> {note.client_name}</div>
                                                <div><span className="text-gray-400">Code client :</span> {note.client_code || '-'}</div>
                                                <div><span className="text-gray-400">Commande :</span> {note.client_order_number}</div>
                                                <div><span className="text-gray-400">Articles :</span> {note.items?.length || 0}</div>
                                                <div><span className="text-gray-400">Total :</span> <span className="font-bold">{(note.delivered_total || note.total || 0).toFixed(2)} EUR</span></div>
                                            </div>
                                            {note.bl_generated_at && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Genere le {new Date(note.bl_generated_at).toLocaleDateString('fr-FR')} a {new Date(note.bl_generated_at).toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                (async () => {
                                                    try {
                                                        const res = await adminAPI.downloadSingleBL(note.id, true);
                                                        const blob = new Blob([res.data], { type: 'application/pdf' });
                                                        const url = URL.createObjectURL(blob);
                                                        const w = window.open(url, '_blank');
                                                        if (!w) toast.error('Autorisez les fenetres pour visualiser');
                                                    } catch { toast.error('Erreur visualisation'); }
                                                })();
                                            }}
                                            variant="outline"
                                            size="icon"
                                            className="rounded-lg h-9 w-9"
                                            title="Visualiser le BL"
                                            data-testid={`view-bl-${note.id}`}
                                        >
                                            <Eye className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                (async () => {
                                                    try {
                                                        const res = await adminAPI.downloadSingleBL(note.id);
                                                        const blob = new Blob([res.data], { type: 'application/pdf' });
                                                        const url = URL.createObjectURL(blob);
                                                        const w = window.open(url);
                                                        if (w) setTimeout(() => w.print(), 500);
                                                    } catch { toast.error('Erreur impression'); }
                                                })();
                                            }}
                                            variant="outline"
                                            size="icon"
                                            className="rounded-lg h-9 w-9"
                                            title="Imprimer"
                                            data-testid={`print-bl-${note.id}`}
                                        >
                                            <Printer className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); handleDownload(note); }}
                                            variant="outline"
                                            className="rounded-lg"
                                            data-testid={`download-bl-${note.id}`}
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            PDF
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
